import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { CameraDevice } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ColumnDef } from '@tanstack/react-table';
import { Radio, AlertTriangle, Info, Phone, Loader2 } from 'lucide-react';

// The SIM page, honest. device_sim is empty — we have no ICCID, data balance,
// signal or carrier for any camera, and there is no top-up integration. What we
// DO have (recovered from v1) is which cameras are 4G, and their connectivity.
// So this shows the 4G fleet by health: the "dead" ones (eye4 says inactivated)
// are the likely SIM/data-lapse cases to reactivate — the real work this page is
// for — while being upfront that the metering/top-up isn't wired.

const cat = (d: CameraDevice): 'online' | 'quiet' | 'dead' | 'offline' =>
  d.status === 'online' ? 'online'
    : d.eye4State === 'active' ? 'quiet'
      : d.eye4State === 'inactivated' ? 'dead'
        : 'offline';

const namePhone = (name: string): string | undefined => {
  const m = (name || '').match(/(\d{7,})/);
  return m ? m[1] : undefined;
};

const relTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  const h = Math.floor(s / 3600);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

export const SimManagementPage: React.FC = () => {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setDevices(await api.getDevicesByConnection('4g'));
      } catch (err) {
        console.error('Failed to load 4G fleet', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = useMemo(() => {
    const c = { online: 0, quiet: 0, dead: 0, offline: 0 };
    devices.forEach((d) => { c[cat(d)]++; });
    return c;
  }, [devices]);

  // Worst first: dead (reactivation) then plain offline, then quiet, then online.
  const rank = { dead: 0, offline: 1, quiet: 2, online: 3 };
  const sorted = useMemo(
    () => [...devices].sort((a, b) => rank[cat(a)] - rank[cat(b)]),
    [devices],
  );

  const columns = useMemo<ColumnDef<CameraDevice>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Camera',
      cell: (info) => {
        const d = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{d.name || '(unnamed)'}</div>
            <code style={{ fontSize: 11, color: 'var(--primary-light)' }}>{d.serial}</code>
          </div>
        );
      },
    },
    {
      accessorKey: 'ownerName',
      header: 'Owner',
      cell: (info) => {
        const d = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{d.ownerName}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{d.ownerEmail}</div>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Connectivity',
      accessorFn: (d) => cat(d),
      cell: (info) => {
        const c = cat(info.row.original);
        return <StatusBadge status={c === 'online' ? 'online' : c === 'quiet' ? 'info' : c === 'dead' ? 'inactivated' : 'offline'} />;
      },
    },
    {
      id: 'signal',
      header: 'SIM / data',
      cell: () => <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>not metered</span>,
    },
    {
      accessorKey: 'lastHeartbeat',
      header: 'Last seen',
      cell: (info) => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{relTime(info.row.original.lastHeartbeat)}</span>,
    },
    {
      id: 'call',
      header: 'Call',
      cell: (info) => {
        const p = namePhone(info.row.original.name);
        return p
          ? <a href={`tel:${p}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}><Phone size={12} /> <span>{p}</span></a>
          : <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>—</span>;
      },
    },
  ], []);

  return (
    <div>
      {/* Honest banner */}
      <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', color: 'var(--warning-text)', borderRadius: 'var(--radius-md)', padding: '11px 16px', marginBottom: 16, fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Info size={15} style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          SIM metering isn't wired yet — there's no ICCID, data balance, signal or carrier on file for any camera, and no top-up integration.
          What we <strong>do</strong> know is which cameras are 4G and whether they're reachable. The <strong>dead</strong> ones are the likely SIM/data-lapse cases to reactivate.
        </span>
      </div>

      {/* 4G fleet health */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', padding: '16px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Radio size={18} color="var(--primary-light)" />
          <div>
            <strong style={{ fontSize: 15 }}>4G fleet — {devices.length} cameras</strong>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Recovered from v1 (connection type). WiFi cameras aren't shown here.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <Stat label="Online" value={counts.online} color="var(--success-text)" />
          <Stat label="Quiet" value={counts.quiet} color="var(--text-muted)" divider />
          <Stat label="Need reactivation" value={counts.dead} color="var(--danger-text)" divider />
          {counts.offline > 0 && <Stat label="Offline (unprobed)" value={counts.offline} color="var(--info-text)" divider />}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={22} className="spin" /></div>
      ) : (
        <DataTable
          storageKey="simManagement"
          data={sorted}
          columns={columns}
          searchPlaceholder="Search camera name, serial, owner..."
          exportFilename="mwm_4g_fleet.csv"
        />
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color: string; divider?: boolean }> = ({ label, value, color, divider }) => (
  <div style={divider ? { borderLeft: '1px solid var(--border-subtle)', paddingLeft: 20 } : undefined}>
    <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);
